import { Link } from 'react-router-dom'
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  FileText,
  Globe2,
  HeartHandshake,
  LayoutTemplate,
  Lock,
  Palette,
  Repeat2,
  ShieldCheck,
  Sparkles,
  XCircle,
  Zap,
} from 'lucide-react'

import { HeroMockup } from '@/components/marketing/hero-mockup'
import { JsonLd } from '@/components/seo/json-ld'
import { SeoHead } from '@/components/seo/seo-head'
import { Button } from '@/components/ui/button'
import { ZERO_FEE_BADGE } from '@/data/pricing-content'
import { createMetadata, getFaqSchema, getProductSchema, marketingSeoPages } from '@/lib/seo'

const steps = [
  {
    step: '1',
    title: 'Create your account',
    description: 'Set up a free workspace for your nonprofit.',
  },
  {
    step: '2',
    title: 'Connect Stripe',
    description: 'Link your own Stripe account so donations flow straight to you.',
  },
  {
    step: '3',
    title: 'Customize and publish',
    description: 'Choose your branding and giving options, then add your form to your website.',
  },
] as const

const painPoints = [
  {
    icon: LayoutTemplate,
    title: 'Clunky donation pages',
    description: 'Slow or off-brand forms add friction, especially on mobile.',
  },
  {
    icon: Globe2,
    title: 'Donors sent off-site',
    description: 'A redirect changes the domain and visual context just before checkout.',
  },
  {
    icon: Repeat2,
    title: 'Recurring giving friction',
    description: 'A hard-to-find monthly option makes recurring giving easy to miss.',
  },
  {
    icon: ShieldCheck,
    title: 'Receipts and records',
    description: 'Donor receipts and reporting get hard to manage by hand.',
  },
] as const

const beforeItems = [
  'A generic third-party checkout link',
  'Donors leave your website to give',
  'Limited donation-specific amounts and layout',
  'No branded receipts or donor records',
] as const

const afterItems = [
  'A branded donation form on your own site',
  'Inline payment fields with a secure hosted fallback',
  'One-time and recurring giving built in',
  'Automatic receipts, donor records, and reporting',
] as const

const featureCards = [
  {
    icon: CreditCard,
    title: 'Donation Forms',
    description: 'Inline forms, popup buttons, and hosted donation pages.',
    points: ['Suggested amounts', 'Mobile layout', 'One-time or monthly'],
  },
  {
    icon: HeartHandshake,
    title: 'Campaign Pages',
    description: 'Launch focused appeals with progress and connected checkout.',
    points: ['Goal tracking', 'Story sections', 'Channel links'],
  },
  {
    icon: Repeat2,
    title: 'Recurring Giving',
    description: 'Offer one-time and monthly giving in the same form.',
    points: ['Monthly option', 'Stripe subscriptions', 'Recurring gift records'],
  },
  {
    icon: Globe2,
    title: 'Copy-Paste Embed',
    description: 'Add the generated two-line snippet where your site supports custom scripts.',
    points: ['WordPress plugin or snippet', 'Compatible site-builder plans', 'Script-based custom HTML'],
  },
  {
    icon: BarChart3,
    title: 'Donations & Reporting',
    description: 'Donor records, receipts, and donation activity at a glance.',
    points: ['Donor records', 'Branded receipts', 'Donation trends'],
  },
  {
    icon: ShieldCheck,
    title: 'Your Own Stripe',
    description: 'Donations flow straight to your Stripe account — no held funds.',
    points: ['Stripe-controlled payouts', 'Secure checkout', 'Transparent fees'],
  },
] as const

const trustFacts = [
  {
    icon: ShieldCheck,
    title: 'Straight to your Stripe',
    description: 'Donations flow directly into your own Stripe account. KindLumen never holds your funds.',
  },
  {
    icon: CreditCard,
    title: 'Powered by Stripe',
    description: 'Checkout runs on Stripe’s secure, PCI-DSS Level 1 payment infrastructure.',
  },
  {
    icon: Lock,
    title: 'SSL-encrypted',
    description: 'Every donation form uses an encrypted checkout connection.',
  },
  {
    icon: BadgeCheck,
    title: 'No paid plan required',
    description: 'KindLumen charges organizations no subscription, setup, or platform fee.',
  },
] as const

const differentiators = [
  {
    icon: Zap,
    title: 'Three-part setup',
    description: 'Connect Stripe, build your form, and add the generated embed without rebuilding your website.',
  },
  {
    icon: Sparkles,
    title: 'Built for your brand',
    description: 'A modern, mobile-first checkout with your logo, colors, and copy.',
  },
  {
    icon: Palette,
    title: 'Brand controls',
    description: 'Your logo, colors, and wording on a form that looks like it belongs on your site.',
  },
  {
    icon: CreditCard,
    title: 'Powered by your Stripe',
    description: 'Donations flow straight into your own Stripe account — no held funds.',
  },
  {
    icon: Repeat2,
    title: 'Recurring built in',
    description: 'Offer one-time and monthly giving in the same form.',
  },
  {
    icon: FileText,
    title: 'Receipts and records',
    description: 'Automatic receipts and clean donor records.',
  },
] as const

const faqItems = [
  {
    question: 'Is KindLumen really a free Stripe donation form?',
    answer:
      'Yes. KindLumen charges organizations no setup fees, monthly fees, or platform fees. You connect your own Stripe account and only standard Stripe processing fees apply — donors can optionally cover them.',
  },
  {
    question: 'Can I add a donation form to my existing website?',
    answer:
      'Yes. Add the generated two-line embed snippet to a page where your site and current plan permit script-based custom HTML. On WordPress, you can instead install the free KindLumen plugin and follow its guided account connection.',
  },
  {
    question: 'Are there limits on forms, donations, or donors?',
    answer:
      'No. KindLumen includes unlimited donation forms, campaigns, donations, and donors on the free plan — no response caps or monthly submission limits.',
  },
  {
    question: 'Do donors have to tip KindLumen?',
    answer:
      'No. Donors are never asked to tip KindLumen. They may optionally cover transaction costs, shown transparently before they complete their donation.',
  },
  {
    question: 'Do donations go to my own Stripe account?',
    answer:
      'Yes. You connect your own Stripe account and donations flow directly to you. KindLumen never holds your funds.',
  },
  {
    question: 'Does KindLumen support recurring donations?',
    answer:
      'Yes. Donors can choose between one-time and monthly giving in the same form.',
  },
  {
    question: 'Which website builders does the embed work with?',
    answer:
      'WordPress supports the free KindLumen plugin or the embed snippet. Builders such as Squarespace, Wix, and Webflow can use the generated two-line snippet when the site’s current plan permits script-based custom HTML. Test the published page because builder rules can change.',
  },
  {
    question: 'Can I send receipts and see donor records?',
    answer:
      'Yes. Automatic branded receipts, donor records, and donation reporting are included from day one.',
  },
  {
    question: 'Can web agencies and developers use KindLumen for client sites?',
    answer:
      'Yes. Agencies can add KindLumen’s generated two-line embed to client sites that permit script-based custom HTML. Each organization connects its own Stripe account, so client funds never route through the agency or KindLumen.',
  },
] as const

const metadata = createMetadata(marketingSeoPages.home)

export default function HomePage() {
  return (
    <div className="overflow-hidden bg-white">
      <SeoHead metadata={metadata} />
      <JsonLd data={getProductSchema()} />
      <JsonLd data={getFaqSchema([...faqItems])} />

      <section className="bg-[linear-gradient(180deg,#EEF7FF_0%,#F7FCFF_48%,#FFFFFF_100%)]">
        <div className="marketing-shell grid items-center gap-12 pb-24 pt-16 lg:min-h-[660px] lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:pt-20">
          <div className="max-w-2xl">
            <div className="eyebrow border-teal/20 bg-teal/10 text-teal">
              For nonprofit websites
            </div>

            <h1 className="mt-6 text-[2.3rem] font-semibold leading-[1.08] text-ink sm:text-[2.9rem] lg:text-[2.45rem] lg:leading-[1.08] xl:text-[3.15rem]">
              Add a <span className="whitespace-nowrap">free Stripe</span> donation form to <span className="gradient-text">your website.</span>
            </h1>

            <p className="mt-5 max-w-lg text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">
              Accept one-time and monthly gifts on the website you already have. Your branding. Your Stripe account. No limits on forms or donations.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="sm:min-w-44">
                <Link to="/signup">Create your free donation form</Link>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <Link to="#how-it-works">See how it works</Link>
              </Button>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-ink-muted">
              <span>{ZERO_FEE_BADGE} · Stripe processing fees apply.</span>
            </div>
          </div>

          <HeroMockup />
        </div>
      </section>

      <section id="how-it-works" className="marketing-shell py-24">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand">How it works</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] text-ink sm:text-5xl">
            Configure and publish in three steps.
          </h2>
          <p className="mt-4 text-base leading-7 text-ink-soft">
            Keep your website and use KindLumen instead of maintaining a custom Stripe integration.
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-5xl gap-5 md:grid-cols-3">
          {steps.map((item) => (
            <div key={item.step} className="rounded-[22px] border border-line bg-white p-6 shadow-[0_18px_50px_rgba(15,35,78,0.06)]">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-sm font-semibold text-white">
                {item.step}
              </div>
              <h3 className="mt-5 text-lg font-semibold text-ink">{item.title}</h3>
              <p className="mt-3 text-sm leading-6 text-ink-soft">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="marketing-shell py-24">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand">The reality</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] text-ink sm:text-5xl">
            Where donation flows create friction.
          </h2>
          <p className="mt-4 text-base leading-7 text-ink-soft">
            Slow pages, abrupt handoffs, and unclear options add work at the moment someone is ready to give.
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {painPoints.map((item) => (
            <div key={item.title} className="rounded-[22px] border border-line bg-white p-6 shadow-[0_18px_50px_rgba(15,35,78,0.06)]">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
                <item.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-ink">{item.title}</h3>
              <p className="mt-3 text-sm leading-6 text-ink-soft">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[radial-gradient(circle_at_20%_10%,rgba(16,185,129,0.18),transparent_28%),linear-gradient(135deg,#101E48,#081127_58%,#082F2D)] py-24 text-white">
        <div className="marketing-shell">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal">The difference</p>
            <h2 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] text-white sm:text-5xl">
              Stripe handles the payment. KindLumen handles the experience.
            </h2>
          </div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-6 lg:grid-cols-2">
            <div className="rounded-[24px] border border-danger/20 bg-danger/10 p-6">
              <p className="text-sm font-semibold text-danger">A raw payment link</p>
              <div className="mt-5 space-y-3">
                {beforeItems.map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm leading-6 text-white/65">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[24px] border border-teal/20 bg-teal/10 p-6">
              <p className="text-sm font-semibold text-teal">With KindLumen</p>
              <div className="mt-5 space-y-3">
                {afterItems.map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm leading-6 text-white/70">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell py-24">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand">Features</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] text-ink sm:text-5xl">
            Everything in your donation form.
          </h2>
        </div>

        <div className="mx-auto mt-12 grid max-w-6xl gap-5 md:grid-cols-2 xl:grid-cols-3">
          {featureCards.map((item) => (
            <div key={item.title} className="rounded-[22px] border border-line bg-white p-6 shadow-[0_18px_50px_rgba(15,35,78,0.05)]">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
                <item.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-ink">{item.title}</h3>
              <p className="mt-3 text-sm leading-6 text-ink-soft">{item.description}</p>
              <div className="mt-5 space-y-2">
                {item.points.map((point) => (
                  <div key={point} className="flex items-center gap-2 text-sm text-ink-soft">
                    <CheckCircle2 className="h-4 w-4 text-teal" />
                    {point}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[linear-gradient(180deg,#FFFFFF,#F5FAFF)] py-24">
        <div className="marketing-shell">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand">Trust &amp; security</p>
            <h2 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] text-ink sm:text-5xl">
              Your money goes straight to your Stripe.
            </h2>
            <p className="mt-4 text-base leading-7 text-ink-soft">
              KindLumen never touches your funds. Donations flow directly into your own Stripe account — on payment infrastructure your donors already trust.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {trustFacts.map((item) => (
              <div key={item.title} className="rounded-[20px] border border-line bg-white px-6 py-7 text-center shadow-[0_16px_42px_rgba(15,35,78,0.05)]">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <item.icon className="h-5 w-5" />
                </div>
                <p className="mt-5 text-lg font-semibold text-ink">{item.title}</p>
                <p className="mt-2 text-sm leading-6 text-ink-soft">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[radial-gradient(circle_at_20%_10%,rgba(16,185,129,0.24),transparent_28%),linear-gradient(135deg,#075E4E,#0B3654_56%,#122756)] py-24 text-white">
        <div className="marketing-shell">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal">Why KindLumen</p>
            <h2 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] text-white sm:text-5xl">
              Fast. Beautiful. Brandable.
            </h2>
            <p className="mt-4 text-base leading-7 text-white/70">
              A Stripe donation form that is quick to launch, embeds on your site, and adapts to your brand.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-6xl gap-5 md:grid-cols-2 xl:grid-cols-3">
            {differentiators.map((item) => (
              <div key={item.title} className="rounded-[22px] border border-white/10 bg-white/10 p-6 backdrop-blur">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-teal">
                  <item.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-white">{item.title}</h3>
                <p className="mt-3 text-sm leading-6 text-white/65">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-shell py-24">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand">FAQ</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] text-ink sm:text-5xl">
            Questions teams ask first.
          </h2>
        </div>

        <div className="mx-auto mt-10 max-w-3xl space-y-3">
          {faqItems.map((item) => (
            <details
              key={item.question}
              className="group rounded-xl border border-line bg-white px-5 py-4 shadow-[0_12px_30px_rgba(15,35,78,0.04)] transition hover:border-brand/30"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-semibold text-ink [&::-webkit-details-marker]:hidden">
                <span>{item.question}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-brand transition-transform duration-200 group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-sm leading-6 text-ink-soft">{item.answer}</p>
            </details>
          ))}
        </div>

        <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center gap-4 text-center">
          <Link to="/agencies" className="text-sm font-semibold text-brand hover:underline">
            Building a client website? Explore KindLumen for agencies →
          </Link>
          <Link to="/resources" className="text-sm font-semibold text-brand hover:underline">
            Browse all guides and resources →
          </Link>
        </div>
      </section>

      <section className="marketing-shell pb-24">
        <div className="mx-auto max-w-4xl rounded-[24px] bg-[radial-gradient(circle_at_18%_18%,rgba(14,165,233,0.28),transparent_28%),linear-gradient(135deg,#2563EB,#0F766E)] px-8 py-14 text-center text-white shadow-[0_28px_80px_rgba(37,99,235,0.22)]">
          <h2 className="text-balance text-4xl font-semibold leading-[1.08] text-white sm:text-5xl">
            Launch your donation form today.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild variant="secondary">
              <Link to="/signup">
                Create your donation form
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="dark">
              <Link to="/pricing">View pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

export const Component = HomePage
